'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { stickingApi } from './sticking.api';
import { stickingKeys } from './sticking.queries';
import { registrationKeys } from '@/features/registration';
import type {
  CreatePageIndexRequest,
  UpdatePageIndexRequest,
  CreatePrintBatchRequest,
  UpdatePrintBatchRequest,
  SetPageCountRequest,
  RequestExtraStickingLabelsRequest,
  AdjustActualPageCountRequest
} from '../types';

export function useCreatePageIndexMutation(registrationId: number) {
  const queryClient = useQueryClient();
  const t = useTranslations('sticking');

  return useMutation({
    mutationFn: (data: CreatePageIndexRequest) =>
      stickingApi.createPageIndex(registrationId, data),
    meta: {
      operationType: 'create',
      fallbackSuccessMessage: t('createSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: stickingKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: stickingKeys.metrics()
      });
      queryClient.invalidateQueries({
        queryKey: registrationKeys.metrics()
      });
      queryClient.invalidateQueries({
        queryKey: registrationKeys.lists()
      });
    }
  });
}

export function useUpdatePageIndexMutation(registrationId: number) {
  const queryClient = useQueryClient();
  const t = useTranslations('sticking');

  return useMutation({
    mutationFn: ({
      pageIndexId,
      data
    }: {
      pageIndexId: number;
      data: UpdatePageIndexRequest;
    }) => stickingApi.updatePageIndex(registrationId, pageIndexId, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('updateSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: stickingKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: stickingKeys.metrics()
      });
      queryClient.invalidateQueries({
        queryKey: registrationKeys.metrics()
      });
      queryClient.invalidateQueries({
        queryKey: registrationKeys.lists()
      });
    }
  });
}

export function useDeletePageIndexMutation(registrationId: number) {
  const queryClient = useQueryClient();
  const t = useTranslations('sticking');

  return useMutation({
    mutationFn: (pageIndexId: number) =>
      stickingApi.deletePageIndex(registrationId, pageIndexId),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('deleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: stickingKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: stickingKeys.metrics()
      });
      queryClient.invalidateQueries({
        queryKey: registrationKeys.metrics()
      });
      queryClient.invalidateQueries({
        queryKey: registrationKeys.lists()
      });
    }
  });
}

export function useBulkDeletePageIndexesMutation(registrationId: number) {
  const queryClient = useQueryClient();
  const t = useTranslations('sticking');

  return useMutation({
    mutationFn: (ids: number[]) =>
      stickingApi.bulkDeletePageIndexes(registrationId, ids),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('bulkDeleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: stickingKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: stickingKeys.metrics()
      });
      queryClient.invalidateQueries({
        queryKey: registrationKeys.metrics()
      });
      queryClient.invalidateQueries({
        queryKey: registrationKeys.lists()
      });
    }
  });
}

export function useUpdatePrintBatchMutation(registrationId: number) {
  const queryClient = useQueryClient();
  const t = useTranslations('sticking');

  return useMutation({
    mutationFn: ({
      batchId,
      data
    }: {
      batchId: number;
      data: UpdatePrintBatchRequest;
    }) => stickingApi.updatePrintBatch(registrationId, batchId, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('printBatchUpdated')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: stickingKeys.printBatches()
      });
      queryClient.invalidateQueries({
        queryKey: stickingKeys.metrics()
      });
      queryClient.invalidateQueries({
        queryKey: registrationKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: registrationKeys.detail(registrationId)
      });
    }
  });
}

export function useCreatePrintBatchMutation(registrationId: number) {
  const queryClient = useQueryClient();
  const t = useTranslations('sticking');

  return useMutation({
    mutationFn: (data: CreatePrintBatchRequest) =>
      stickingApi.createPrintBatch(registrationId, data),
    meta: {
      operationType: 'create',
      fallbackSuccessMessage: t('printBatchCreated')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: stickingKeys.printBatches()
      });
      queryClient.invalidateQueries({
        queryKey: stickingKeys.metrics()
      });
    }
  });
}

// ─── Sticking-stage workflow mutations ──────────────────────────────────

export function useSetPageCountMutation(registrationId: number) {
  const queryClient = useQueryClient();
  const t = useTranslations('sticking');

  return useMutation({
    mutationFn: (data: SetPageCountRequest) =>
      stickingApi.setPageCount(registrationId, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('setPageCountSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: registrationKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: registrationKeys.detail(registrationId)
      });
      queryClient.invalidateQueries({ queryKey: stickingKeys.metrics() });
    }
  });
}

export function useRequestExtraStickingLabelsMutation(registrationId: number) {
  const queryClient = useQueryClient();
  const t = useTranslations('sticking');

  return useMutation({
    mutationFn: (data: RequestExtraStickingLabelsRequest) =>
      stickingApi.requestExtraStickingLabels(registrationId, data),
    meta: {
      operationType: 'create',
      fallbackSuccessMessage: t('requestExtraSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stickingKeys.printBatches() });
      queryClient.invalidateQueries({
        queryKey: registrationKeys.detail(registrationId)
      });
      queryClient.invalidateQueries({ queryKey: registrationKeys.lists() });
    }
  });
}

export function useAdjustActualPageCountMutation(registrationId: number) {
  const queryClient = useQueryClient();
  const t = useTranslations('sticking');

  return useMutation({
    mutationFn: (data: AdjustActualPageCountRequest) =>
      stickingApi.adjustActualPageCount(registrationId, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('adjustActualSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: registrationKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: registrationKeys.detail(registrationId)
      });
    }
  });
}

export function useReturnToRegistrationMutation(registrationId: number) {
  const queryClient = useQueryClient();
  const t = useTranslations('sticking');

  return useMutation({
    mutationFn: () => stickingApi.returnToRegistration(registrationId),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('returnedToRegistration')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: registrationKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: registrationKeys.detail(registrationId)
      });
      queryClient.invalidateQueries({ queryKey: stickingKeys.lists() });
    }
  });
}
